// The sidebar's "Setup · n left" (spec/13 §2, §13.1; F-HQ-03): an owner sees Setup while their organisation has a venue
// still setting up — or none yet — and it leads to that Setup. Its count is the **essentials** still missing — a first
// venue, and each venue in setup's opening hours, price and paired PC — never a suggested step, which would nag
// forever; with only suggested steps left there is no count. It goes away once every venue has opened. Other staff
// never see it: setting a venue up is the owner's job, and the Setup pages stay one link away from the settings for
// anyone who may read them.

import type { OrganizationSetup } from '@clavian/api-client';

import type { MyOrganization, MyVenue } from '../api/me';
import { isOwnerRole } from './landing';
import { useOrganizationSetup } from './queries';

export interface SetupNavEntry {
  /** Essentials left: a first venue if there is none, and each venue in setup's missing essentials. */
  readonly left: number;
}

/** Is there anything to set up: a venue of the organisation still in setup, or no venue at all? */
export function organizationSettingUp(organizationId: string, venues: readonly MyVenue[]): boolean {
  const own = venues.filter((venue) => venue.organizationId === organizationId);
  return own.length === 0 || own.some((venue) => venue.status === 'setup');
}

/** The essentials left across the organisation's setup, as the server reports them. A suggested step never counts. */
export function essentialsLeft(setup: OrganizationSetup): number {
  const own = setup.steps.filter((step) => step.kind === 'essential' && step.status !== 'done').length;
  return own + setup.venues.filter((venue) => venue.status === 'setup').reduce((sum, venue) => sum + venue.missingEssentials.length, 0);
}

/** The entry for this member and organisation, or `undefined` when there is none to show (yet). */
export function useSetupNavEntry(organization: MyOrganization | undefined, venues: readonly MyVenue[] | undefined): SetupNavEntry | undefined {
  const shown = organization !== undefined && venues !== undefined && isOwnerRole(organization.role) && organizationSettingUp(organization.id, venues);
  const setup = useOrganizationSetup(organization?.id ?? '', shown);
  if (!shown || setup.data === undefined) return undefined;
  return { left: essentialsLeft(setup.data) };
}
